import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { 
  Plus, 
  Trash2, 
  Edit2, 
  Save,
  Link2,
  AlertCircle,
  Search,
  Check,
  Zap,
  Users,
  X
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerMappings = () => {
  const [mappedCustomers, setMappedCustomers] = useState([]);
  const [unmappedSheetCustomers, setUnmappedSheetCustomers] = useState([]);
  const [sheetTabs, setSheetTabs] = useState([]);
  const [appCustomers, setAppCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [stats, setStats] = useState({ total: 0, mapped: 0, unmapped: 0 });
  
  const [formData, setFormData] = useState({
    app_name: "",
    sheet_name: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [allMappingsRes, tabsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/all-customer-mappings`),
        axios.get(`${API}/admin/sheet-tab-names`),
        axios.get(`${API}/users`)
      ]);
      
      // Set mapped customers (from Overall Pay Sheet that have matching tabs)
      setMappedCustomers(allMappingsRes.data.mapped_customers || []);
      // Set unmapped sheet customers (from Overall Pay Sheet WITHOUT matching tabs)
      setUnmappedSheetCustomers(allMappingsRes.data.unmapped_sheet_customers || []);
      setStats({
        total: allMappingsRes.data.total_customers || 0,
        mapped: allMappingsRes.data.mapped_count || 0,
        unmapped: allMappingsRes.data.unmapped_count || 0
      });
      
      setSheetTabs(tabsRes.data.tabs || []);
      
      // Get customer users for dropdown
      const customerUsers = usersRes.data.filter(u => u.role === 'customer');
      setAppCustomers(customerUsers);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load customer mappings");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.app_name || !formData.sheet_name) {
      toast.error("Please fill in both customer name and sheet tab");
      return;
    }
    
    try {
      if (editingMapping?.id && editingMapping.match_type === 'manual') {
        // Update existing manual mapping
        await axios.put(`${API}/admin/customer-mappings/${editingMapping.id}`, formData);
        toast.success("Mapping updated successfully");
      } else {
        // Create new manual mapping (for auto mappings, this creates an override)
        await axios.post(`${API}/admin/customer-mappings`, formData);
        toast.success("Mapping created successfully");
      }
      
      setFormData({ app_name: "", sheet_name: "" });
      setEditingMapping(null);
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to save mapping";
      toast.error(message);
    }
  };

  const handleEditMapping = (mapping) => {
    setFormData({
      app_name: mapping.customer_name,
      sheet_name: mapping.tab_name
    });
    setEditingMapping(mapping);
    setDialogOpen(true);
  };

  const handleDeleteMapping = async (mapping) => {
    if (mapping.match_type !== 'manual') {
      toast.error("Only manual mappings can be deleted. To change an auto-mapping, create a manual override.");
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete the mapping for "${mapping.customer_name}"?`)) return;
    
    try {
      await axios.delete(`${API}/admin/customer-mappings/${mapping.id}`);
      toast.success("Mapping deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete mapping");
    }
  };

  const handleCreateMappingForUnmapped = (customerName) => {
    setFormData({
      app_name: customerName,
      sheet_name: ""
    });
    setEditingMapping(null);
    setDialogOpen(true);
  };

  const filteredMappings = mappedCustomers.filter(m =>
    m.customer_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.tab_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getTypeColor = (type) => {
    switch (type) {
      case 'manual':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'exact':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'partial':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'manual':
        return 'Manual';
      case 'exact':
        return 'Auto (Exact)';
      case 'partial':
        return 'Auto (Partial)';
      default:
        return type;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#f4cc1e]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 bg-[#f4cc1e] p-4 sm:p-6 rounded-xl min-h-screen" data-testid="customer-mappings">
      {/* Header */}
      <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-white font-['Chivo']">Customer Name Mappings</h1>
              <p className="text-white/60 mt-1">
                Map customer account names to their Google Sheet tab names
              </p>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  onClick={() => {
                    setFormData({ app_name: "", sheet_name: "" });
                    setEditingMapping(null);
                  }}
                  className="bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/90 rounded-xl font-semibold"
                >
                  <Plus size={18} className="mr-2" />
                  Add Mapping
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#091c3c] border-[#1a3a6a]">
                <DialogHeader>
                  <DialogTitle className="text-white font-['Chivo']">
                    {editingMapping ? "Edit Mapping" : "Create Mapping"}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-white/80">Customer Name (from Pay Sheet)</Label>
                    <Input
                      value={formData.app_name}
                      onChange={(e) => setFormData({ ...formData, app_name: e.target.value })}
                      placeholder="Enter customer name"
                      className="bg-[#0d2a5a] border-[#1a3a6a] text-white"
                    />
                    <p className="text-xs text-white/50">The customer name as it appears in the Overall Pay Sheet</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white/80">Google Sheet Tab Name</Label>
                    <Select
                      value={formData.sheet_name}
                      onValueChange={(value) => setFormData({ ...formData, sheet_name: value })}
                    >
                      <SelectTrigger className="bg-[#0d2a5a] border-[#1a3a6a] text-white">
                        <SelectValue placeholder="Select sheet tab" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#091c3c] border-[#1a3a6a] max-h-60">
                        {sheetTabs.map((tab) => (
                          <SelectItem key={tab} value={tab} className="text-white">
                            {tab}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-white/50">Select from available Google Sheet tabs</p>
                  </div>
                  <div className="flex gap-3 pt-2">
                    <Button
                      type="submit"
                      className="flex-1 bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/90"
                    >
                      <Save size={16} className="mr-2" />
                      {editingMapping ? "Update" : "Create"}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setDialogOpen(false)}
                      className="border-[#1a3a6a] text-white hover:bg-[#1a3a6a]"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          
          {/* Stats Row */}
          <div className="grid grid-cols-3 gap-4 mt-6">
            <div className="bg-[#0d2a5a] rounded-xl p-4 text-center">
              <Users size={20} className="mx-auto text-white/60 mb-1" />
              <p className="text-2xl font-bold text-white">{stats.total}</p>
              <p className="text-xs text-white/50">Total Customers</p>
            </div>
            <div className="bg-[#0d2a5a] rounded-xl p-4 text-center">
              <Check size={20} className="mx-auto text-green-400 mb-1" />
              <p className="text-2xl font-bold text-green-400">{stats.mapped}</p>
              <p className="text-xs text-white/50">Mapped</p>
            </div>
            <div className="bg-[#0d2a5a] rounded-xl p-4 text-center">
              <AlertCircle size={20} className="mx-auto text-orange-400 mb-1" />
              <p className="text-2xl font-bold text-orange-400">{stats.unmapped}</p>
              <p className="text-xs text-white/50">Unmapped</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Unmapped Sheet Customers (from Overall Pay Sheet without matching tabs) */}
      {unmappedSheetCustomers.length > 0 && (
        <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg">
          <CardHeader className="border-b border-[#1a3a6a] pb-4">
            <CardTitle className="text-orange-400 flex items-center gap-2 text-base font-['Chivo']">
              <AlertCircle size={18} />
              Customers Without Matching Tabs ({unmappedSheetCustomers.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <p className="text-white/50 text-sm mb-4">
              These customers appear in the Overall Pay Sheet but don't have a matching tab in Google Sheets. Click "Map" to create a manual mapping.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[400px] overflow-auto">
              {unmappedSheetCustomers.map((customer, idx) => (
                <div key={idx} className="bg-[#0d2a5a] rounded-xl p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-white font-medium text-sm truncate">{customer.customer_name}</p>
                      {customer.area && (
                        <p className="text-[10px] text-white/40 truncate">{customer.area}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {customer.order_appointments > 0 && (
                        <Badge className="bg-[#f4cc1e]/20 text-[#f4cc1e] border-[#f4cc1e]/30 text-[10px]">
                          {customer.order_appointments} appts
                        </Badge>
                      )}
                      <Button
                        size="sm"
                        onClick={() => handleCreateMappingForUnmapped(customer.customer_name)}
                        className="h-7 px-2 bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/90 text-xs"
                      >
                        <Plus size={12} className="mr-1" />
                        Map
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Mapped Customers (from Overall Pay Sheet with matching tabs) */}
      <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg">
        <CardHeader className="border-b border-[#1a3a6a] pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="font-['Chivo'] text-white flex items-center gap-2">
              <Link2 size={18} className="text-[#f4cc1e]" />
              Active Mappings ({mappedCustomers.length})
            </CardTitle>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <Input
                placeholder="Search mappings..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-8 w-48 bg-[#0d2a5a] border-[#1a3a6a] text-white text-sm"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filteredMappings.length === 0 ? (
            <div className="p-8 text-center text-white/50">
              <Link2 size={40} className="mx-auto mb-3 opacity-30" />
              <p>No mappings found</p>
              <p className="text-sm mt-1">Mappings will appear here once customers are matched to tabs</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-[#1a3a6a]">
                    <TableHead className="text-[#f4cc1e] uppercase text-xs">Customer Name</TableHead>
                    <TableHead className="text-[#f4cc1e] uppercase text-xs">Sheet Tab Name</TableHead>
                    <TableHead className="text-[#f4cc1e] uppercase text-xs">Match Type</TableHead>
                    <TableHead className="text-[#f4cc1e] uppercase text-xs w-24">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMappings.map((mapping, idx) => (
                    <TableRow key={idx} className="border-b border-[#1a3a6a] hover:bg-[#0d2a5a]">
                      <TableCell>
                        <p className="text-white font-medium">{mapping.customer_name}</p>
                      </TableCell>
                      <TableCell className="text-white/80">{mapping.tab_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-[10px] ${getTypeColor(mapping.match_type)}`}>
                          {mapping.match_type === 'exact' ? <Check size={10} className="mr-1" /> : 
                           mapping.match_type === 'manual' ? <Edit2 size={10} className="mr-1" /> :
                           <Zap size={10} className="mr-1" />}
                          {getTypeLabel(mapping.match_type)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleEditMapping(mapping)}
                            className="p-1.5 rounded hover:bg-[#1a3a6a] text-white/60 hover:text-white transition-colors"
                            title={mapping.match_type === 'manual' ? "Edit mapping" : "Create manual override"}
                          >
                            <Edit2 size={14} />
                          </button>
                          {mapping.match_type === 'manual' && (
                            <button
                              onClick={() => handleDeleteMapping(mapping)}
                              className="p-1.5 rounded hover:bg-red-500/20 text-white/60 hover:text-red-400 transition-colors"
                              title="Delete mapping"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomerMappings;
